import { Button, Heading } from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './Audiences.module.css';
import { LeadsContactsToolbarButtons } from './LeadsContactsToolbarButtons';
import { useLeadContactsFilter } from './use-lead-contacts-filter';
import { isBilling2023V2Subscription } from '@mm/state/model/SubscriptionModel';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { MyLeadsAttributes } from '@mm/state/interfaces/myLeads';
import { ContactsByChannelsStatistic } from './ContactsByChannelsStatistic/ContactsByChannelsStatistic';
import { AudienceListAccordions } from './AudienceListAccordions/AudienceListAccordions';
import { useFetchAudienceContacts } from './use-fetch-audience-contacts';
import { snake } from '@mm/state/helpers/converterString';
import { snakeCase } from 'lodash';
import ObjectUtils from '../../../../../common/helpers/utils/object-utils';
import { useFetchAudience } from './use-fetch-audience';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';

export const PAGE_SIZE = 10;

export const getESStructure = (esFilters?: MyLeadsAttributes) => {
  if (!esFilters) return;
  const { attributes, ...restVariables } = esFilters || {};

  return {
    ...(attributes && { attributes: snake(attributes) }),
    ...Object.entries(restVariables)
      .flatMap(([key, value]) => ({
        [snakeCase(key).toLowerCase()]: ObjectUtils.deepSnakeKeys(
          value || {},
          true,
          true,
        ),
      }))
      .reduce((result, item) => ({ ...result, ...item }), {}),
  };
};

export const Audiences = () => {
  const { activeAudienceId } = useLeadContactsFilter();
  const { retrieveAll } = useContactsCRUDActions();
  const facebookPage = useSelector(selectActiveFacebookPage);
  const allContacts = useSelector(selectContacts);

  const { fetchPage, isFetching } = useFetchAudienceContacts(activeAudienceId, {
    pageSize: PAGE_SIZE,
    skipBusy: true,
    pauseRefreshingWhenEditingAudience: true,
    withDeliverable: true,
  });
  const { busy } = useFetchAudience(activeAudienceId, {
    handleError: true,
    options: { skipBusy: true },
    queryParams: { inUse: true },
  });

  const modalActions = useModalActions();
  const { showSlideoutMenu } = useViewActions();

  const processClick = (onSuccess: (args?: any) => void) => {
    if (
      facebookPage?.subscription &&
      facebookPage?.subscription?.failedPayment &&
      isBilling2023V2Subscription(facebookPage.subscription)
    ) {
      modalActions.openAccountLockedModal();
    } else {
      onSuccess();
    }
  };

  const openFiltersSlideout = () => {
    showSlideoutMenu('myLeadsFilters');
  };

  useEffect(() => {
    if (facebookPage) {
      retrieveAll(facebookPage?.id);
    }
  }, []);

  return (
    <section className={styles.container}>
      <div className={styles.contactsPane}>
        <div className={styles.mainToolbar}>
          <div className={styles.mainToolbarLeft}>
            <Heading size={3} className={styles.audienceName}>
              Audiences
            </Heading>
          </div>

          <LeadsContactsToolbarButtons processClick={processClick} />
        </div>

        <div className={styles.analyticsContainer}>
          <Button
            color="blue"
            css={{ container: styles.save }}
            onClick={openFiltersSlideout}
          >
            Build an Audience
          </Button>

          <div className={styles.analyticsLeadsCount}>
            Leads {allContacts.allContactsCount?.toLocaleString()}
          </div>
          <ContactsByChannelsStatistic
            css={{
              container: styles.statsContainer,
              row: styles.statRow,
              data: styles.statData,
              count: styles.statCount,
            }}
          />
        </div>
        <AudienceListAccordions
          processClick={processClick}
          fetchContacts={fetchPage}
          onCreateClick={openFiltersSlideout}
          activeAudienceId={activeAudienceId}
          busy={isFetching || busy}
        />
      </div>
    </section>
  );
};
